<script lang="ts">
	import { format } from 'svelte-i18n';
	import { Container, Text } from '@Shelly/shelly-design-system/index';
	import { classMap } from '@Shelly/shelly-design-system/helpers/component-utils';
	import { real_id } from 'src/helpers/interface_helpers';
	import { getDeviceStatus, getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { convertAndFormatDistance } from 'src/helpers/distance_helper';

	$: settingsStore = getDeviceSettings(device.id);
	export let device;
	export let deviceData;
	export let hasMb = false;

	$: distanceUnit = device?.ht_ui?.unit || $settingsStore?.ht_ui?.unit || 'mm';
	$: modeType = deviceData ? deviceData.modeType : device?.modeType;
	$: solidsType = deviceData ? deviceData.solidsType : device?.solidsType;
	$: solidsTypeValue = typeof solidsType === 'string' ? solidsType : solidsType?.value;
	$: statusStore = getDeviceStatus(device.id);
	$: distanceValue = $statusStore?.['distance:0']?.cvalue;
	$: clearanceToShow =
		distanceValue > 0
			? convertAndFormatDistance(distanceValue, distanceUnit, device.type)
			: $format('common_out_of_range', {
					default: 'Out of range',
				});
	$: liquidLevel = distanceValue > 0 ? $statusStore?.['distance:0']?.percent || 0 : 0;
	$: totalDepth = device?.meterRangeStart - device?.meterRangeEnd;
	$: liquidDepth =
		distanceValue > 0
			? convertAndFormatDistance((liquidLevel / 100) * totalDepth, distanceUnit, device.type)
			: 0;
</script>

<div
	class={classMap({ 'main-tab-first-inner-container': hasMb })}
	style="--level: {modeType === 'liquids' ? Math.max(0, liquidLevel - 3) : liquidLevel}%;"
>
	<Container padding={hasMb ? 'm' : '0'}>
		<div class="content-background light d-flex justify-content-center align-items-center">
			<div class="mr-xl">
				<div
					class="measurement-item mb-m pb-m d-flex flex-direction-column justify-content-center align-items-center text-center"
				>
					<Text class="mb-s text-color-3"
						>{modeType === 'liquids'
							? $format('label_liquid_level', { default: 'Liquid Level' })
							: $format('label_solids_level', {
									default: 'Solids Level',
								})}</Text
					>
					<Text styleType="h1" class="text-300">
						{liquidLevel} %
					</Text>
				</div>
				<div
					class="measurement-item mb-m pb-m d-flex flex-direction-column justify-content-center align-items-center text-center"
				>
					<Text class="mb-s text-color-3"
						>{modeType === 'liquids'
							? $format('label_liquid_depth', { default: 'Liquid Depth' })
							: $format('label_solids_depth', {
									default: 'Solids Depth',
								})}</Text
					>
					<Text styleType="h1" class="text-300">{liquidDepth}</Text>
				</div>
				<div
					class="measurement-item d-flex flex-direction-column justify-content-center align-items-center text-center"
				>
					<Text class="mb-s text-color-3"
						>{$format('label_clearance', { default: 'Clearance' })}</Text
					>
					<Text styleType="h1" class="text-300">{clearanceToShow}</Text>
				</div>
			</div>

			<div
				class={`liquid-column ${classMap({
					'minimize-liquid-column': !hasMb,
					'solids-generic-type':
						modeType !== 'liquids' &&
						(!solidsTypeValue || solidsTypeValue === 'Generic'),
					'solids-pellets-type':
						modeType !== 'liquids' && solidsTypeValue === 'Pellets/Feed',
					'solids-grain-type': modeType !== 'liquids' && solidsTypeValue === 'Grain',
					'solids-sand-type': modeType !== 'liquids' && solidsTypeValue === 'Salt/Sand',
				})}`}
			>
				{#if distanceValue > 0 && modeType === 'liquids'}
					<img
						src="./images/onboarding/distance/wave.svg"
						class="wave"
						alt={$format('alt_wave_effect', { default: 'Wave effect' })}
					/>
					<img
						src="./images/onboarding/distance/wave.svg"
						class="wave"
						alt={$format('alt_wave_effect', { default: 'Wave effect' })}
					/>
				{/if}
				<div
					class={`liquid-fill ${classMap({
						'solids-generic-type':
							modeType !== 'liquids' &&
							(!solidsTypeValue || solidsTypeValue === 'Generic'),
						'solids-pellets-type':
							modeType !== 'liquids' && solidsTypeValue === 'Pellets/Feed',
						'solids-grain-type': modeType !== 'liquids' && solidsTypeValue === 'Grain',
						'solids-sand-type': modeType !== 'liquids' && solidsTypeValue === 'Salt/Sand',
					})}`}
				/>
				<div class="marker-10" />
				<div class="marker-20" />
				<div class="marker-30" />
				<div class="marker-40" />
				<div class="marker-50" />
				<div class="marker-60" />
				<div class="marker-70" />
				<div class="marker-80" />
				<div class="marker-90" />
			</div>
		</div>
	</Container>
</div>

<style>
	.main-tab-first-inner-container {
		margin-bottom: 12px;
	}

	.measurement-item {
		max-width: 135px;
		border-bottom: 1px solid #333333;
	}

	.measurement-item:last-child {
		border-bottom: none;
	}

	.liquid-column {
		width: 60px;
		height: 300px;
		border-radius: 20px;
		background: linear-gradient(to top, #025a72 var(--level), #ffffff var(--level));
		position: relative;
		overflow: hidden;
		opacity: 95%;
	}

	.liquid-column.solids-generic-type,
	.liquid-column.solids-pellets-type,
	.liquid-column.solids-grain-type,
	.liquid-column.solids-sand-type {
		background: #ffffff;
	}

	.minimize-liquid-column {
		scale: 0.8;
	}

	/* Blue liquid (covers markers inside) */
	.liquid-fill {
		position: absolute;
		bottom: 0;
		left: 0;
		width: 100%;
		height: var(--level);
		background: #025a72;
		z-index: 2; /* above markers */
	}

	.liquid-fill.solids-generic-type,
	.liquid-fill.solids-pellets-type,
	.liquid-fill.solids-grain-type,
	.liquid-fill.solids-sand-type {
		background-color: white;
		background-size: 500%;
		border-top: 1px solid lightgray;
	}

	.liquid-fill.solids-generic-type {
		background-image: url('/images/onboarding/distance/solids_generic.png');
	}

	.liquid-fill.solids-pellets-type {
		background-image: url('/images/onboarding/distance/solids_pellets.png');
	}

	.liquid-fill.solids-grain-type {
		background-image: url('/images/onboarding/distance/solids_grain.png');
	}

	.liquid-fill.solids-sand-type {
		background-image: url('/images/onboarding/distance/solids_sand.png');
	}

	.wave {
		position: absolute;
		bottom: calc(var(--level) - 0.2%);
		left: -20px;
		width: 200px;
		height: 20px;
		animation: wave 7s cubic-bezier(0.36, 0.45, 0.63, 0.53) 0s infinite;
		z-index: 2; /* above markers */
		user-select: none;
	}

	.wave:nth-of-type(2) {
		bottom: calc(var(--level) - 0.2%);
		right: 0;
		width: 200px;
		height: 20px;
		animation: wave 7s cubic-bezier(0.36, 0.45, 0.63, 0.53) -1.5s infinite;
		z-index: 2; /* above markers */
		user-select: none;
	}

	@keyframes wave {
		0% {
			margin-left: -20px;
		}
		50% {
			margin-left: -100px;
		}
		100% {
			margin-left: -20px;
		}
	}

	/* Extra markers */
	.marker-10,
	.marker-20,
	.marker-30,
	.marker-40,
	.marker-50,
	.marker-60,
	.marker-70,
	.marker-80,
	.marker-90 {
		position: absolute;
		left: 40%;
		right: 40%;
		height: 1px;
		background: lightgray;
		z-index: 1;
	}

	.marker-10 {
		top: 10%;
	}

	.marker-20 {
		top: 20%;
	}

	.marker-30 {
		top: 30%;
	}

	.marker-40 {
		top: 40%;
	}

	.marker-50 {
		top: 50%;
	}

	.marker-60 {
		top: 60%;
	}

	.marker-70 {
		top: 70%;
	}

	.marker-80 {
		top: 80%;
	}

	.marker-90 {
		top: 90%;
	}

	/* Add label text */
	.marker-10::after,
	.marker-20::after,
	.marker-30::after,
	.marker-40::after,
	.marker-50::after,
	.marker-60::after,
	.marker-70::after,
	.marker-80::after,
	.marker-90::after {
		position: absolute;
		bottom: -5px;
		left: 100%; /* or adjust based on where you want it */
		color: #999;
		font-size: 10px;
		margin-left: 2px;
	}

	/* Individual labels */
	.marker-10::after {
		content: '90%';
		text-wrap: nowrap;
	}
	.marker-20::after {
		content: '80%';
		text-wrap: nowrap;
	}
	.marker-30::after {
		content: '70%';
		text-wrap: nowrap;
	}
	.marker-40::after {
		content: '60%';
		text-wrap: nowrap;
	}
	.marker-50::after {
		content: '50%';
		text-wrap: nowrap;
	}
	.marker-60::after {
		content: '40%';
		text-wrap: nowrap;
	}
	.marker-70::after {
		content: '30%';
		text-wrap: nowrap;
	}
	.marker-80::after {
		content: '20%';
		text-wrap: nowrap;
	}
	.marker-90::after {
		content: '10%';
		text-wrap: nowrap;
	}
</style>
